import { useState } from 'react';
import { Grid, Card, CardContent, Stack, Typography, Box, Chip, LinearProgress, Divider, Alert } from '@mui/material';
import { AccountTree, Assignment, ReceiptLong, Savings, Warning, TrendingUp } from '@mui/icons-material';
import { EpcProjectSelect } from '../../components/EpcProjectSelect';
import { useQuery } from '@tanstack/react-query';
import { PageHeader } from '../../components/PageHeader';
import { StatCard } from '../../components/StatCard';
import { epcDashboardApi } from '../../api/epcDashboard.api';

const inr = (n = 0) => `₹${Number(n).toLocaleString('en-IN')}`;
const compact = (n = 0) => (n >= 1e7 ? `₹${(n / 1e7).toFixed(2)}Cr` : n >= 1e5 ? `₹${(n / 1e5).toFixed(2)}L` : inr(n));

function StatusChips({ title, map }: { title: string; map: Record<string, number> }) {
  const entries = Object.entries(map ?? {});
  return (
    <Card variant="outlined" sx={{ height: '100%' }}>
      <CardContent>
        <Typography variant="subtitle2" gutterBottom>{title}</Typography>
        {entries.length === 0 ? <Typography variant="body2" color="text.secondary">No data.</Typography> : (
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {entries.map(([k, v]) => <Chip key={k} size="small" label={`${k.replace(/_/g, ' ')}: ${v}`} />)}
          </Stack>
        )}
      </CardContent>
    </Card>
  );
}

function BudgetBar({ label, value, max, color }: { label: string; value: number; max: number; color: string }) {
  const pct = max > 0 ? Math.round((value / max) * 100) : 0;
  return (
    <Box sx={{ mb: 1.5 }}>
      <Stack direction="row" justifyContent="space-between"><Typography variant="body2">{label}</Typography><Typography variant="body2" fontWeight={600}>{compact(value)}</Typography></Stack>
      <LinearProgress variant="determinate" value={Math.min(pct, 100)} sx={{ height: 8, borderRadius: 1, '& .MuiLinearProgress-bar': { backgroundColor: color } }} />
    </Box>
  );
}

export function EpcDashboardPage() {
  const [projectId, setProjectId] = useState('');
  const pid = projectId ? Number(projectId) : undefined;
  const { data, isLoading } = useQuery({ queryKey: ['epc-dashboard', pid], queryFn: () => epcDashboardApi.overview(pid) });

  const k = data?.kpis;
  const c = data?.costVsBudget;
  const budgetMax = c ? Math.max(c.boqApproved, c.poOrdered, c.workOrders, c.invoiced, c.collected, 1) : 1;

  return (
    <>
      <PageHeader title="EPC Dashboard" subtitle="Portfolio health, cost-vs-budget and delivery pipeline across EPC projects." />
      <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
        <EpcProjectSelect value={projectId} onChange={(v) => setProjectId(v)} label="Project (optional)" helperText="Leave blank for all EPC projects" />
      </Stack>

      {isLoading && <LinearProgress sx={{ mb: 2 }} />}

      {k && (
        <>
          <Grid container spacing={2}>
            <Grid item xs={6} md={3}><StatCard label="Active Projects" value={k.activeProjects} icon={<AccountTree />} color="#1e40af" /></Grid>
            <Grid item xs={6} md={3}><StatCard label="Overall Progress" value={`${k.overallProgressPct}%`} icon={<TrendingUp />} color="#0369a1" hint={`${k.milestonesCompleted}/${k.milestonesTotal} milestones done`} /></Grid>
            <Grid item xs={6} md={3}><StatCard label="Invoiced" value={compact(k.invoicedValue)} icon={<ReceiptLong />} color="#15803d" hint={`Collected ${compact(k.collectedValue)}`} /></Grid>
            <Grid item xs={6} md={3}><StatCard label="Outstanding" value={compact(k.outstandingValue)} icon={<Warning />} color="#b45309" hint="Approved, not yet paid" /></Grid>
            <Grid item xs={6} md={3}><StatCard label="Retention Held" value={compact(k.retentionHeld)} icon={<Savings />} color="#6d28d9" /></Grid>
            <Grid item xs={6} md={3}><StatCard label="Handovers Pending" value={k.handoversPending} icon={<Assignment />} color="#0e7490" /></Grid>
            <Grid item xs={6} md={3}><StatCard label="Overdue Milestones" value={k.overdueMilestones} icon={<Warning />} color={k.overdueMilestones > 0 ? '#b91c1c' : '#64748b'} /></Grid>
          </Grid>

          {k.overdueMilestones > 0 && <Alert severity="warning" sx={{ mt: 2 }}>{k.overdueMilestones} milestone(s) are past their planned end date and not completed.</Alert>}

          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12} md={6}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="subtitle1" gutterBottom fontWeight={600}>Cost vs Budget</Typography>
                  {c && (
                    <>
                      <BudgetBar label="BOQ approved (scope)" value={c.boqApproved} max={budgetMax} color="#1e40af" />
                      <BudgetBar label="PO ordered (procurement)" value={c.poOrdered} max={budgetMax} color="#7c3aed" />
                      <BudgetBar label="Work orders (subcontract)" value={c.workOrders} max={budgetMax} color="#0891b2" />
                      <BudgetBar label="Invoiced to client" value={c.invoiced} max={budgetMax} color="#15803d" />
                      <BudgetBar label="Collected from client" value={c.collected} max={budgetMax} color="#16a34a" />
                      <Divider sx={{ my: 1.5 }} />
                      <Stack direction="row" justifyContent="space-between"><Typography variant="body2">Vendor/tech approved</Typography><Typography variant="body2">{compact(c.vendorApproved)}</Typography></Stack>
                      <Stack direction="row" justifyContent="space-between"><Typography variant="body2">Vendor/tech paid</Typography><Typography variant="body2" fontWeight={600}>{compact(c.vendorPaid)}</Typography></Stack>
                    </>
                  )}
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}><StatusChips title="Milestones" map={data.breakdowns.milestonesByStatus} /></Grid>
                <Grid item xs={12} sm={6}><StatusChips title="Purchase Orders" map={data.breakdowns.purchaseOrdersByStatus} /></Grid>
                <Grid item xs={12} sm={6}><StatusChips title="Work Orders" map={data.breakdowns.workOrdersByStatus} /></Grid>
                <Grid item xs={12} sm={6}><StatusChips title="Invoices" map={data.breakdowns.invoicesByStatus} /></Grid>
              </Grid>
            </Grid>
          </Grid>
        </>
      )}
    </>
  );
}
